import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "@/core/api";
import { dayjs } from "@/core/utils/date";
import { flushWorkbookCellEdits } from "@/payroll/ai-script/workbook-cell-edit-batcher";
import {
  WORKBOOK_EXPORT_STATUS,
  requestAiScriptWorkbookExport,
  validateWorkbookExportOptions,
} from "@/payroll/ai-script/workbook-export";
import {
  type DownloadAiScriptWorkbookParams,
  WORKBOOK_EXPORT_POLL_RETRIES,
  WORKBOOK_EXPORT_TIMEOUT,
  WORKBOOK_EXPORT_TIMEOUT_MESSAGE,
  type WorkbookExportRequest,
  aiScriptWorkbookExportKey,
  getWorkbookExportPollDelay,
  hasWorkbookExportExpired,
  isTerminalExportError,
  saveWorkbookFile,
} from "./workbook-export-poll";

export const useDownloadAiScriptWorkbook = (businessId: string | null) => {
  const api = useApi();
  const attempts = useRef(0);
  const settledAttempt = useRef(0);
  const tokenRef = useRef<string | undefined>(undefined);
  const [request, setRequest] = useState<WorkbookExportRequest | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [isComplete, setIsComplete] = useState(false);
  const startedAt = request?.startedAt;

  const isCurrentBusiness = !!request && request.businessId === businessId;

  const { data, error: queryError } = useQuery({
    queryKey: aiScriptWorkbookExportKey(
      request?.businessId ?? businessId,
      request?.runId ?? null,
      request?.attempt ?? 0,
    ),
    enabled: !!businessId && isCurrentBusiness,
    queryFn: async ({ signal }) => {
      if (!request) throw new Error("Business is required");
      await flushWorkbookCellEdits(request.businessId, request.runId, {
        rejectOnError: true,
      });
      signal.throwIfAborted();
      if (hasWorkbookExportExpired(request.startedAt)) {
        throw new Error(WORKBOOK_EXPORT_TIMEOUT_MESSAGE);
      }

      return requestAiScriptWorkbookExport(
        api,
        request.businessId,
        request.runId,
        signal,
        tokenRef.current,
        request,
      );
    },
    refetchInterval: (query) => {
      if (!startedAt || hasWorkbookExportExpired(startedAt)) return false;
      if (
        query.state.data?.status === WORKBOOK_EXPORT_STATUS.ready ||
        isTerminalExportError(query.state.error) ||
        query.state.error?.message === WORKBOOK_EXPORT_TIMEOUT_MESSAGE
      ) {
        return false;
      }

      return getWorkbookExportPollDelay(
        query.state.dataUpdateCount,
        dayjs().diff(startedAt, "milliseconds"),
      );
    },
    refetchIntervalInBackground: true,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) =>
      error instanceof Error &&
      error.message !== WORKBOOK_EXPORT_TIMEOUT_MESSAGE &&
      !isTerminalExportError(error) &&
      failureCount < WORKBOOK_EXPORT_POLL_RETRIES,
    networkMode: "always",
  });

  useEffect(() => {
    if (data?.status === WORKBOOK_EXPORT_STATUS.processing && data.token) {
      tokenRef.current = data.token;
    }
  }, [data]);

  useEffect(() => {
    if (!request) return undefined;

    const remaining = Math.max(
      0,
      WORKBOOK_EXPORT_TIMEOUT - dayjs().diff(request.startedAt, "milliseconds"),
    );
    const timer = setTimeout(() => {
      if (settledAttempt.current === request.attempt) return;

      settledAttempt.current = request.attempt;
      setError(new Error(WORKBOOK_EXPORT_TIMEOUT_MESSAGE));
      setRequest(null);
    }, remaining);

    return () => clearTimeout(timer);
  }, [request]);

  useEffect(() => {
    if (!request || settledAttempt.current === request.attempt) return;

    if (data?.status === WORKBOOK_EXPORT_STATUS.ready) {
      settledAttempt.current = request.attempt;
      saveWorkbookFile(data.file, data.filename ?? request.filename);
      setIsComplete(true);
      setRequest(null);
      return;
    }

    if (queryError) {
      settledAttempt.current = request.attempt;
      setError(queryError);
      setRequest(null);
    }
  }, [data, queryError, request]);

  useEffect(() => {
    if (!request || isCurrentBusiness) return;

    tokenRef.current = undefined;
    settledAttempt.current = request.attempt;
    setRequest(null);
    setError(null);
    setIsComplete(false);
  }, [request, isCurrentBusiness]);

  const download = useCallback(
    (params: DownloadAiScriptWorkbookParams) => {
      if (!businessId) return;

      setIsComplete(false);
      try {
        validateWorkbookExportOptions(params);
      } catch (validationError) {
        setError(validationError);
        return;
      }
      attempts.current += 1;
      tokenRef.current = undefined;
      setError(null);
      setRequest({
        ...params,
        sheetIds: params.sheetIds ? [...params.sheetIds] : undefined,
        attempt: attempts.current,
        startedAt: dayjs(),
        businessId,
      });
    },
    [businessId],
  );

  return {
    download,
    isDownloading: !!request,
    isPreparing: data?.status === WORKBOOK_EXPORT_STATUS.processing,
    error,
    isComplete,
  };
};
